import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { LimeGreen } from '@karehero/llama/theme/mui/color';
import { radius } from '@karehero/llama/theme/radius';

import HomeIndicator, {
  HOME_INDICATOR_H,
} from '@/designs-dev/device/HomeIndicator';

export type TabBarItem = {
  id: string;
  label: string;
  icon: string;
  // whether the icon has a filled (fas) variant registered in llama
  solid: boolean;
};

// Default member-app tab bar (Figma KHBottomNavigation). 'Search' is
// intentionally omitted. A prototype can pass its own `items` without changing
// this default.
//
// Profile and Care are two tabs, not one: Profile is the record of the person
// being cared for (calls, docs, forms, tools), Care is where the family gets on
// with arranging care. The tab that used to be Care held the record, which left
// arranging care buried in a tool. Shortlists V1 proposed the split; this is it.
/**
 * What a scroller under the floating (neutral) bar needs at its foot, so its
 * last row can scroll clear of the pill.
 */
export const TAB_BAR_FLOAT_ROOM = HOME_INDICATOR_H + 60 + 8 + 24;

export const DEFAULT_ITEMS: TabBarItem[] = [
  { id: 'home', label: 'Home', icon: 'house', solid: true },
  { id: 'actions', label: 'Actions', icon: 'calendar', solid: true },
  // Outline even when active: `solid` means llama has a filled variant
  // registered, and there is no solid `user` in it. Adding one needs
  // pro-solid-svg-icons in llama, which is not a dependency of this app.
  { id: 'profile', label: 'Profile', icon: 'user', solid: false },
  { id: 'care', label: 'Care', icon: 'heart', solid: true },
  // Help, not Chat. A tab called Chat asks somebody who is stuck to work out
  // whether their problem is a talking problem before they can go anywhere;
  // Help takes them all, and chat is the first thing inside it.
  { id: 'help', label: 'Help', icon: 'circle-question', solid: false },
];

const TabBar = ({
  active = 'home',
  onSelect,
  items = DEFAULT_ITEMS,
  tone = 'lime',
  action,
}: {
  active?: string;
  onSelect?: (id: string) => void;
  items?: TabBarItem[];
  /**
   * Lime as main ships it, or neutral: Gardenia with a hairline on top and a
   * neutral pill, so the bar stops drawing the eye. A trial on the Care hub
   * concept; a variant here rather than a second tab bar, so the two cannot
   * drift.
   */
  tone?: 'lime' | 'neutral';
  /**
   * A button for the tab's main thing to do, like Create action, right-aligned
   * above the pill and inside the fade, so the page goes under both. Neutral
   * only: the lime band has no fade to sit in.
   */
  action?: ReactNode;
}) => {
  const neutral = tone === 'neutral';
  const activeIndex = items.findIndex((it) => it.id === active);
  const cellWidth = 100 / items.length;

  return (
    // Colours come from llama's LimeGreen ramp directly, not the theme's
    // `custom.limeGreen.*`. The palette preview (palettePreview.ts) collapses
    // lime to a single value per the new Figma foundations, which would make
    // the track, the pill and the idle labels all the same hex — the pill and
    // the inactive tabs disappear. Device chrome should keep matching main
    // while that collapse is still being evaluated, so it reads the ramp.
    <Box
      sx={
        neutral
          ? {
              // Floating over the page, which scrolls under it. The fade
              // takes the content out before it reaches the pill, the way
              // iOS's glass bars do. The parent needs position: relative and
              // TAB_BAR_FLOAT_ROOM of padding at the foot of its scroller.
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 5,
              pt: 3,
              background: (t) =>
                `linear-gradient(to bottom, ${alpha(
                  t.palette.background.default,
                  0,
                )}, ${t.palette.background.default} 45%)`,
            }
          : { flexShrink: 0, bgcolor: LimeGreen[200] }
      }
    >
      {neutral && action && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 1 }}>
          {action}
        </Box>
      )}
      {/* Neutral floats: a Floral White pill at 55%, see-through and
          blurred, with an edge, inset from the sides. Lime is the full-width
          band main ships. */}
      <Box
        sx={
          neutral
            ? {
                mx: 2,
                mt: 1,
                px: 0.75,
                bgcolor: (t) => alpha(t.palette.background.paper, 0.55),
                backdropFilter: 'blur(16px)',
                border: 1,
                borderColor: 'custom.neutral.200',
                borderRadius: 999,
              }
            : undefined
        }
      >
        <Box
          sx={{
            mx: neutral ? 0 : 1.5,
            position: 'relative',
            height: neutral ? 60 : 55,
          }}
        >
          {/* sliding active pill */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              // Taller on the floating bar: the icon and label stack to about
              // 48px, which spilled out of a 45px pill once it was a different
              // colour from the bar.
              height: neutral ? 52 : 45,
              width: `${cellWidth}%`,
              // Pale cherry, solid: the hub's one accent, soft enough to stay
              // quiet. Gardenia and grey fills were either lost or grey.
              bgcolor: (t) =>
                neutral
                  ? lighten(t.palette.custom.cherry[500], 0.9)
                  : LimeGreen[500],
              borderRadius: `${radius.xxl}px`,
              zIndex: 0,
              left: `${Math.max(activeIndex, 0) * cellWidth}%`,
              transform: 'translateY(-50%)',
              opacity: activeIndex < 0 ? 0 : 1,
              transition: 'left 320ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />

          {/* tab cells */}
          <Stack
            sx={{ height: '100%', position: 'relative', zIndex: 1 }}
            direction='row'
          >
            {items.map((it) => {
              const isActive = it.id === active;
              const color = neutral
                ? isActive
                  ? 'text.primary'
                  : 'text.secondary'
                : isActive
                  ? 'primary.main'
                  : LimeGreen[700];
              return (
                <Box
                  key={it.id}
                  onClick={() => onSelect?.(it.id)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: onSelect ? 'pointer' : 'default',
                  }}
                >
                  <Box
                    sx={{
                      color: color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      transition: 'color 220ms ease',
                    }}
                  >
                    <Icon
                      icon={it.icon as never}
                      type={isActive && it.solid ? 'fas' : 'fal'}
                      fontSize={22}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',

                      letterSpacing: '0.4px',
                      mt: '4px',
                      color,
                      transition: 'color 220ms ease',
                    }}
                  >
                    {it.label}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
      <HomeIndicator />
    </Box>
  );
};

export default TabBar;
